import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useToast } from '../common/Toast';
import { useConfirm } from '../common/ConfirmDialog';
import CustomSelect from '../common/CustomSelect';
import InlinePillSelect, { PillCaret } from '../common/InlinePillSelect';
import UserSelect from '../common/UserSelect';
import MarkdownEditor from '../common/MarkdownEditor';
import MarkdownViewer from '../common/MarkdownViewer';
import Avatar from '../common/Avatar';
import MentionInput from '../beta/tickets/MentionInput';
import { useBetaData } from '../../contexts/BetaDataContext';
import { setWorkItemRefById, setWorkItemRefByNo } from '../../utils/workItemUrlParams';
import WorkItemReferences from './WorkItemReferences';
import { getTextColorForBg } from '../../utils/textUtils';
import {
  getWorkItem, getWorkItemChildren, updateWorkItem, createWorkItem, deleteWorkItem,
  addWorkItemAssignee, removeWorkItemAssignee, setWorkItemOwner, setWorkItemAcceptanceResult,
  getWorkItemDeliveryLogs, createWorkItemDeliveryLog, updateWorkItemDeliveryLog, deleteWorkItemDeliveryLog,
  getWorkItemComments, createWorkItemComment, updateWorkItemComment, deleteWorkItemComment,
  getWorkItemAttachments, uploadWorkItemAttachment, getWorkItemAttachmentUrl, deleteWorkItemAttachment,
  uploadWorkItemCommentAttachment, getWorkItemAttachmentsByIds, sweepStaleCommentDrafts,
  getAllChildrenForOrder, updateWorkItemOrder,
  getMyWorkItemWatchState, setWorkItemWatchState,
} from '../../api/workItems';
import { buildAttachmentMarkup, parseAttachmentIds } from '../../utils/workItemAttachmentMarkup';
import { compareWorkItemSiblings, mergeSiblingOrder } from '../../constants/workItem';
import { getAuditLogs } from '../../api/auditLog';
import { getAllCompanies } from '../../api/companies';
import {
  createAutopilotRequest, getAutopilotRequestForWorkItem, discussAutopilotRequest, getAutopilotStatus,
  listPreviewAccounts, getAutopilotMessages,
} from '../../api/autopilot';
import AutopilotDiffViewer from './AutopilotDiffViewer';
import { AUTOPILOT_STATUS_LABEL } from '../../constants/autopilot';
import { AC_CATEGORY_ORDER, AC_CATEGORY_META, AC_STATUS_META } from '../../constants/acceptance';
import { canEditWorkItem, canDeleteDeliveryLog } from '../../middleware/betaPermissions';
import {
  WORK_ITEM_TYPES, WORK_ITEM_TYPE_LABELS, WORK_ITEM_TYPE_COLORS,
  WORK_ITEM_PRIORITY_META, WORK_ITEM_ROLES, WORK_ITEM_ROLE_COLORS,
  DELIVERY_LABELS, DELIVERY_LABEL_COLORS, ENV_OPTIONS, ENV_LABELS,
} from '../../constants/workItem';
import './WorkItemDetailDrawer.scss';

// 交付紀錄標籤/環境已抽到 constants/workItem.js 共用(DELIVERY_LABELS / ENV_OPTIONS …)
const AVA_COLORS = ['#dd2590', '#6938ef', '#0e7090', '#039855', '#b54708', '#4f46e5'];
const avaColor = (id) => AVA_COLORS[[...String(id || '')].reduce((a, c) => a + c.charCodeAt(0), 0) % AVA_COLORS.length];
const fmtTime = (d) => (d ? new Date(d).toLocaleString('zh-TW', { timeZone: 'Asia/Taipei', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }) : '');
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('zh-TW', { timeZone: 'Asia/Taipei', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }) : '');

// 內嵌 pill 下拉(值以 Figma pill/文字呈現,點擊開選單)
const CaretIcon = ({ color = 'currentColor' }) => (
  <svg className="wd-pill-caret" width="9" height="9" viewBox="0 0 12 12" fill="none" stroke={color} strokeWidth="2"><path d="M3 5l3 3 3-3" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

const InlineSelect = ({ trigger, options, value, onChange, caret = true }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const openMenu = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 6, left: r.left });
    setOpen(true);
  };
  return (
    <div className="wd-inline">
      <button ref={btnRef} className="wd-inline-trigger" onClick={() => (open ? setOpen(false) : openMenu())}>{trigger}{caret && <span className="wd-caret">▾</span>}</button>
      {open && pos && ReactDOM.createPortal(
        <>
          <div className="wd-inline-ov" onClick={() => setOpen(false)} />
          <div className="wd-inline-menu" style={{ top: pos.top, left: pos.left }}>
            {options.map((o) => (
              <button key={String(o.value)} className={o.value === value ? 'active' : ''} onClick={() => { onChange(o.value); setOpen(false); }}>
                {o.color && <span className="wd-opt-dot" style={{ background: o.color }} />}
                {o.label}
              </button>
            ))}
          </div>
        </>, document.body)}
    </div>
  );
};

// 輕量子項編輯面板(小視窗;子項不走完整 detail)
const SubItemPanel = ({ sub, companyUsers, profile, canEdit = true, onClose, onSaved, onDirtyChange }) => {
  const toast = useToast();
  // Owner = 最早 assigned_at（與列表/主 drawer 一致；否則重開會顯示到非 owner 的協作者）
  const existing = [...(sub.assignees || [])].sort((a, b) => new Date(a.assigned_at || 0) - new Date(b.assigned_at || 0)).map((a) => a.user_id);
  const [form, setForm] = useState({ title: sub.title || '', is_completed: !!sub.is_completed, role: sub.role || null, start_date: sub.start_date || '', end_date: sub.end_date || '', description: sub.description || '', estimated_hours: sub.estimated_hours ?? '', actual_hours: sub.actual_hours ?? '' });
  const [ownerId, setOwnerId] = useState(existing[0] || null);
  const [saving, setSaving] = useState(false);
  // 回報「是否有未儲存變更」給父層（切換子項前用來提示）
  const initialRef = useRef({
    formJson: JSON.stringify({ title: sub.title || '', is_completed: !!sub.is_completed, role: sub.role || null, start_date: sub.start_date || '', end_date: sub.end_date || '', description: sub.description || '', estimated_hours: sub.estimated_hours ?? '', actual_hours: sub.actual_hours ?? '' }),
    ownerId: existing[0] || null,
  });
  useEffect(() => {
    const dirty = JSON.stringify(form) !== initialRef.current.formJson || ownerId !== initialRef.current.ownerId;
    onDirtyChange?.(dirty);
  }, [form, ownerId, onDirtyChange]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!canEdit) { toast.error('沒有編輯權限'); return; }
    if (!form.title.trim()) { toast.error('請輸入標題'); return; }
    setSaving(true);
    // 子項無詳情頁可手動改狀態,完成狀態切換時同步 status(closed/open),避免母項關閉後子項狀態卡在 open 一直出現在篩選內
    await updateWorkItem(sub.id, {
      title: form.title.trim(), is_completed: form.is_completed, role: form.role,
      status: form.is_completed ? 'closed' : 'open', completed_at: form.is_completed ? new Date().toISOString() : null,
      start_date: form.start_date || null, end_date: form.end_date || null, description: form.description || null,
      estimated_hours: form.estimated_hours === '' || form.estimated_hours == null ? null : Number(form.estimated_hours),
      actual_hours: form.actual_hours === '' || form.actual_hours == null ? null : Number(form.actual_hours),
    });
    if (ownerId && ownerId !== existing[0]) await setWorkItemOwner(sub.id, ownerId, profile?.id);
    setSaving(false); onSaved();
  };
  return (
    <div className="wd-subcard" role="dialog" aria-label="編輯子項">
      <div className="wd-subcard-inner">
        <div className="wd-subpanel-head"><span className="wd-subpanel-title">編輯子項</span><button className="wd-close-x" onClick={onClose}>✕</button></div>
        <div className="wd-subpanel-body">
          <label className="wd-sp-field"><span className="wd-sb-label">標題</span><input autoFocus value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="子項標題" /></label>
          <div className="wd-sp-2col">
            <div className="wd-sp-field"><span className="wd-sb-label">完成狀態</span>
              <button type="button" className={`wd-toggle ${form.is_completed ? 'on' : ''}`} onClick={() => set('is_completed', !form.is_completed)}><span className="wd-toggle-track"><span className="wd-toggle-thumb" /></span>{form.is_completed ? '已完成' : '未完成'}</button>
            </div>
            <div className="wd-sp-field"><span className="wd-sb-label">角色</span>
              <CustomSelect value={form.role} onChange={(v) => set('role', v)} options={[{ value: null, label: '未指定' }, ...WORK_ITEM_ROLES.map((r) => ({ value: r, label: r }))]} portal />
            </div>
          </div>
          <div className="wd-sp-field"><span className="wd-sb-label">負責人</span>
            <UserSelect values={ownerId ? [ownerId] : []} onChange={(ids) => setOwnerId(ids[0] || null)} users={companyUsers} singleSelect placeholder="選擇負責人…" />
          </div>
          <div className="wd-sp-2col">
            <label className="wd-sp-field"><span className="wd-sb-label">開始</span><input type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} /></label>
            <label className="wd-sp-field"><span className="wd-sb-label">結束</span><input type="date" value={form.end_date} onChange={(e) => set('end_date', e.target.value)} /></label>
          </div>
          <div className="wd-sp-2col">
            <label className="wd-sp-field"><span className="wd-sb-label">預估時數</span><input type="number" min="0" step="0.5" value={form.estimated_hours} onChange={(e) => set('estimated_hours', e.target.value)} placeholder="小時" /></label>
            <label className="wd-sp-field"><span className="wd-sb-label">實際時數</span><input type="number" min="0" step="0.5" value={form.actual_hours} onChange={(e) => set('actual_hours', e.target.value)} placeholder="小時" /></label>
          </div>
          <div className="wd-sp-field"><span className="wd-sb-label">說明</span>
            <MarkdownEditor value={form.description} onChange={(v) => set('description', v)} height="160px" placeholder="子項說明（支援 Markdown）…" linkTickets={false} />
          </div>
        </div>
        <div className="wd-subpanel-foot">
          <button className="wd-btn-secondary" onClick={onClose} disabled={saving}>取消</button>
          <button className="wd-btn-primary" onClick={save} disabled={saving || !form.title.trim()}>{saving ? '儲存中…' : '儲存'}</button>
        </div>
      </div>
    </div>
  );
};

const WorkItemDetailDrawer = ({ itemId, initialSubId, companyUsers, groups, profile, companyId, departmentId, onClose, onChanged, onOpenItem }) => {
  const toast = useToast();
  const { confirm } = useConfirm();
  const { statusOptions, projects } = useBetaData();
  const statusMeta = (v) => (statusOptions || []).find((s) => s.value === v) || { value: v, label: v, color: '#94a3b8' };
  const [item, setItem] = useState(null);
  const [children, setChildren] = useState([]);
  const [logs, setLogs] = useState([]);
  const [comments, setComments] = useState([]);
  // 留言內文引用到的附件 metadata(id → row)。渲染 chip 用,查不到就是已移除。
  const [commentAttachments, setCommentAttachments] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentDraft, setEditCommentDraft] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [editingSub, setEditingSub] = useState(null); // 點子項開的輕量面板
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [discTab, setDiscTab] = useState('comments');
  const [apRequest, setApRequest] = useState(null); // 這張工作項目目前(最新)的 AI 修復需求
  const [apLoading, setApLoading] = useState(false);
  const [apPipelineStatus, setApPipelineStatus] = useState(null); // /status 輪詢結果(pipeline 進度)
  const [apStatusLoading, setApStatusLoading] = useState(false); // 正在抓 apPipelineStatus,跟「抓完發現沒有」要分開判斷
  const [apSending, setApSending] = useState(false);
  const [apDiscussText, setApDiscussText] = useState('');
  const [apPreviewAccounts, setApPreviewAccounts] = useState([]); // 該專案登記的預覽測試帳號(送修角色下拉用)
  const [apPreviewRole, setApPreviewRole] = useState(''); // 送修時選的「以哪個角色預覽」(preview_test_accounts.id)
  const [apMessages, setApMessages] = useState([]); // 該需求的補充說明(討論串)
  const [acSaving, setAcSaving] = useState(null); // 正在儲存的驗收條件 id(防連點)
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState('');
  const [newSubTitle, setNewSubTitle] = useState('');
  const [addingLog, setAddingLog] = useState(false);
  const [logForm, setLogForm] = useState({ label: 'Frontend', version: '', environment: 'staging', note: '' });
  const [logFiles, setLogFiles] = useState([]); // 新增/編輯交付紀錄時待上傳的附件
  const [editingLogId, setEditingLogId] = useState(null);
  const logFileRef = useRef(null);
  const [assignMode, setAssignMode] = useState(null); // null | 'owner' | 'add'

  const usersById = new Map((companyUsers || []).map((u) => [u.id, u]));

  const load = useCallback(async () => {
    if (!itemId) return;
    setLoading(true);
    const [wi, ch, dl, cm, at, ac] = await Promise.all([
      getWorkItem(itemId), getWorkItemChildren(itemId), getWorkItemDeliveryLogs(itemId), getWorkItemComments(itemId),
      getWorkItemAttachments(itemId), getAuditLogs({ resourceType: 'work_item', resourceId: itemId, limit: 100 }),
    ]);
    setItem(wi.data); setChildren(ch.data || []); setLogs(dl.data || []); setComments(cm.data || []);
    // 清掉這張工作項目上「拖了檔案但留言沒送出」的過期殘留(fire-and-forget,失敗不影響開啟)
    sweepStaleCommentDrafts(itemId);
    setAttachments(at.data || []); setActivities(ac.data || []);
    if (wi.data) { setTitleDraft(wi.data.title); setDescDraft(wi.data.description || ''); }
    setLoading(false);
  }, [itemId]);
  useEffect(() => { load(); }, [load]);

  // 追蹤狀態(留言通知):null = 走預設(指派者追蹤/其他人不追蹤),row = 使用者的覆寫
  const [watchRow, setWatchRow] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setWatchRow(null);
    if (itemId && profile?.id) {
      getMyWorkItemWatchState(itemId, profile.id).then(({ data }) => { if (!cancelled) setWatchRow(data); });
    }
    return () => { cancelled = true; };
  }, [itemId, profile?.id]);

  // 換 item 時,若目前停在「規格/驗收」分頁但新 item 沒有該內容(非 specforge / 無驗收),
  // 分頁按鈕會消失但 tab state 還停在舊值 → 內容區空白。退回概覽避免空畫面。
  useEffect(() => {
    if (!item) return;
    const specOk = item.source === 'specforge' && (item.screen_md || item.data_structure_md || item.ui_html);
    const acOk = Array.isArray(item.acceptance_criteria) && item.acceptance_criteria.length > 0;
    if ((tab === 'spec' && !specOk) || (tab === 'acceptance' && !acOk)) setTab('overview');
  }, [item, tab]);

  // 這個專案有設定 autopilot_config 才需要查(沒設定的專案不會顯示「AI 修復」tab,不用打 API)
  const apEnabled = !!item?.projects?.autopilot_config?.gitlabProjectPath;
  // 預覽截圖登入型態(三態):backend-jwt 要選角色、none 免登入、未設不截圖
  const previewAuthType = item?.projects?.autopilot_config?.previewAuth?.type;
  const projectId = item?.projects?.id;

  // backend-jwt 型的專案才需要載入測試帳號清單(給送修角色下拉);其餘型態不用選角色
  useEffect(() => {
    if (!apEnabled || previewAuthType !== 'backend-jwt' || !projectId) { setApPreviewAccounts([]); return; }
    let alive = true;
    listPreviewAccounts(projectId).then(({ data }) => {
      if (alive && data?.accounts) setApPreviewAccounts(data.accounts);
    });
    return () => { alive = false; };
  }, [apEnabled, previewAuthType, projectId]);

  // 載入該需求的補充說明(討論串),讓使用者看得到當初打了什麼
  const loadApMessages = useCallback(async () => {
    if (!apRequest?.id) { setApMessages([]); return; }
    const { data } = await getAutopilotMessages(apRequest.id);
    if (data?.messages) setApMessages(data.messages);
  }, [apRequest?.id]);
  useEffect(() => { loadApMessages(); }, [loadApMessages]);
  const loadApRequest = useCallback(async () => {
    if (!itemId || !apEnabled) return;
    setApLoading(true);
    const { data } = await getAutopilotRequestForWorkItem(itemId);
    setApRequest(data);
    setApPipelineStatus(null);
    // 有 pipeline_id 才代表下面那個 effect 會去抓 diff/檔案清單——這裡先標成「正在抓」,
    // 免得中間空一格畫面被誤判成「已經確認沒有改動」(apPipelineStatus 剛被清成 null,
    // 跟「抓完發現真的沒有」是兩種不同狀態,不能共用同一個 null 判斷)。
    setApStatusLoading(!!data?.pipeline_id);
    setApLoading(false);
  }, [itemId, apEnabled]);
  useEffect(() => { loadApRequest(); }, [loadApRequest]);

  // 輪詢 pipeline 進度。除了 running 期間,還要在「已 committed 但截圖(preview_shots)還沒回來」時
  // 繼續輪——截圖是改完後另一條 pipeline 非同步回填的,輪詢若在 committed 就停,截圖進來畫面不會自己刷
  // (使用者觀察到的「diff 有更新、截圖 card 不會」就是這個)。有上限防呆(截圖被 skip 時不會無限輪)。
  useEffect(() => {
    if (!apRequest || !apRequest.pipeline_id) { setApPipelineStatus(null); setApStatusLoading(false); return; }
    let alive = true;
    let timer = null;
    let shotWaits = 0; // 等截圖的輪次上限(~3.5 分),避免截圖被 skip 時無限輪
    const tick = async () => {
      const { data } = await getAutopilotStatus({
        projectPath: apRequest.project_path, pipelineId: apRequest.pipeline_id, branch: apRequest.branch,
      });
      if (!alive || !data) return;
      setApPipelineStatus(data);
      setApStatusLoading(false);
      const running = apRequest.status === 'running';
      if (running && ['success', 'failed', 'canceled'].includes(data.pipeline?.status)) {
        loadApRequest(); // pipeline 跑完 → 重查 request 拿最新 status(committed/failed)
      }
      // 截圖待回填:有影響路由但還沒 preview_shots → 繼續等(到上限為止)
      const shotsPending = (data.affectedRoutes?.length > 0) && !(data.previewShots?.length > 0);
      if (!running) {
        if (shotsPending && shotWaits < 42) { shotWaits += 1; }
        else if (timer) { clearInterval(timer); timer = null; }
      }
    };
    tick();
    timer = setInterval(tick, 5000);
    return () => { alive = false; if (timer) clearInterval(timer); };
  }, [apRequest, loadApRequest]);

  const sendAiFix = async () => {
    const ok = await confirm({
      title: '送 AI 修復',
      message: `AI 會依這張工作項目的標題/描述直接改 code 並推回 main。確定送出「${item.title}」?`,
      confirmText: '送出',
    });
    if (!ok) return;
    const spec = `${item.title}\n\n${item.description || ''}`.trim();
    setApSending(true);
    const { error } = await createAutopilotRequest({ pmsWorkItemId: itemId, spec, previewRole: apPreviewRole || undefined });
    setApSending(false);
    if (error) { toast.error(error); return; }
    toast.success('已送出,AI 開始處理');
    loadApRequest();
  };

  const sendDiscuss = async () => {
    const text = apDiscussText.trim();
    if (!text || !apRequest) return;
    setApSending(true);
    const { error } = await discussAutopilotRequest(apRequest.id, text);
    setApSending(false);
    if (error) { toast.error(error); return; }
    setApDiscussText('');
    toast.success('已送出,AI 繼續處理');
    loadApRequest();
    loadApMessages();
  };


  // 從列表點子項進來:children 載入後自動彈出該子項的輕量 card(只做一次)
  const subOpenedRef = useRef(false);
  useEffect(() => {
    if (!initialSubId || subOpenedRef.current) return;
    const child = children.find((c) => c.id === initialSubId);
    if (child) { subOpenedRef.current = true; setEditingSub(child); }
  }, [initialSubId, children]);

  // Esc 關閉(子項 card 開啟時,先關 card)
  useEffect(() => {
    const h = (e) => {
      if (e.key !== 'Escape') return;
      if (editingSub) { setEditingSub(null); return; }
      onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose, editingSub]);

  const canEdit = item ? canEditWorkItem(profile, item) : false;

  // 重載活動紀錄（操作後即時反映，不需重開 drawer）
  const reloadActivities = async () => { const { data } = await getAuditLogs({ resourceType: 'work_item', resourceId: itemId, limit: 100 }); setActivities(data || []); };

  const patch = async (updates) => {
    if (!canEdit) { toast.error('沒有編輯此工作項目的權限'); return; }
    // 父項切 closed 且底下有未完成子項 → 提示確認,一併結束子項(避免子項卡未完成、孤兒浮到列表未分組)
    if (updates.status === 'closed') {
      const openChildren = children.filter((c) => !c.is_completed);
      if (openChildren.length > 0) {
        const ok = await confirm({
          title: '結束工作項目',
          message: `底下還有 ${openChildren.length} 個未完成的子項。結案會一併將它們標記完成並關閉,確定嗎?`,
          confirmText: '一併結束',
          type: 'warning',
        });
        if (!ok) return;
        const now = new Date().toISOString();
        await Promise.all(openChildren.map((c) =>
          updateWorkItem(c.id, { is_completed: true, status: 'closed', completed_at: now })));
        setChildren((prev) => prev.map((c) => (c.is_completed ? c : { ...c, is_completed: true, status: 'closed', completed_at: now })));
      }
    }
    const { data, error } = await updateWorkItem(itemId, updates);
    if (error) { toast.error('更新失敗'); return; }
    setItem((prev) => ({ ...prev, ...data }));
    reloadActivities();
    onChanged?.(data); // 帶最新 row,搜尋檢視可原地更新列表(不重抓不跳頁)
  };
  // 對稱護欄:closed 父項若出現未完成子項 → 自動重新開啟父項(否則子項又變孤兒浮到列表未分組)
  const ensureParentReopen = async (childList) => {
    if (item?.status === 'closed' && childList.some((c) => !c.is_completed)) {
      await updateWorkItem(itemId, { status: 'in_progress', completed_at: null });
      setItem((prev) => ({ ...prev, status: 'in_progress', completed_at: null }));
      reloadActivities();
      toast.info('已重新開啟父項(仍有未完成子項)');
    }
  };
  const copyLink = async () => {
    // 直接從 item 組出正規形式 ?cid=<公司slug>&wino=<work_item_no>,讓連結編號跟畫面 #號一致
    // (比照 Ticket);點列表開 drawer 不經 URL,所以不能靠網址正規化,要自己組。
    // cid slug = 公司名小寫(對齊 BetaLayout)。參數清理由 utils/workItemUrlParams 負責。
    const sp = new URLSearchParams(window.location.search);
    let built = false;
    if (item?.work_item_no != null && item?.company_id) {
      try {
        const { data: companies } = await getAllCompanies();
        const slug = companies?.find((c) => c.id === item.company_id)?.name?.toLowerCase().trim();
        if (slug) { setWorkItemRefByNo(sp, item.work_item_no, slug); built = true; }
      } catch { /* 拿不到公司 → 走保底 */ }
    }
    if (!built) setWorkItemRefById(sp, itemId); // 保底:子項/無公司名 → 用 id
    sp.set('v', 'new');
    const url = `${window.location.origin}${window.location.pathname}?${sp.toString()}`;
    try { await navigator.clipboard.writeText(url); toast.success('已複製連結'); } catch { toast.error('複製失敗'); }
  };

  // 追蹤/取消追蹤:指派者預設追蹤(可 mute),其他人預設不追蹤(可 watch)
  const amIAssignee = (item?.assignees || []).some((a) => a.user_id === profile?.id);
  const isWatching = watchRow ? watchRow.state === 'watch' : amIAssignee;
  const toggleWatch = async () => {
    if (!profile?.id) return;
    const next = !isWatching;
    const { data, error } = await setWorkItemWatchState(itemId, profile.id, next, amIAssignee);
    if (error) { toast.error('操作失敗'); return; }
    setWatchRow(data);
    toast.success(next ? '已追蹤,此工作項目有新留言時會通知你' : '已取消追蹤,不再收到此工作項目的留言通知');
  };

  const reloadItem = async () => { const { data } = await getWorkItem(itemId); if (data) setItem(data); reloadActivities(); onChanged?.(data || undefined); };
  // QA 逐條驗收:再點同一個狀態 = 取消。以 API 回傳的完整 row 更新 item(acceptance_results 跟著更新)。
  const toggleAcceptance = async (criterionId, status) => {
    if (!item) return;
    const cur = item.acceptance_results?.[criterionId]?.status;
    const next = cur === status ? null : status;
    setAcSaving(criterionId);
    const { data, error } = await setWorkItemAcceptanceResult(itemId, criterionId, next);
    setAcSaving(null);
    if (error) { toast.error('驗收狀態儲存失敗'); return; }
    if (data) setItem(data);
  };
  const changeOwner = async (newId) => { setAssignMode(null); if (!canEdit) { toast.error('沒有編輯權限'); return; } if (!newId) return; await setWorkItemOwner(itemId, newId, profile?.id); await reloadItem(); };
  const removeAssignee = async (userId) => { if (!canEdit) { toast.error('沒有編輯權限'); return; } await removeWorkItemAssignee(itemId, userId); await reloadItem(); };
  const changeCollaborators = async (newIds, ownerId) => {
    if (!canEdit) { toast.error('沒有編輯權限'); return; }
    const cur = (item.assignees || []).map((a) => a.user_id).filter((id) => id !== ownerId);
    for (const id of newIds.filter((id) => !cur.includes(id))) await addWorkItemAssignee(itemId, id, profile?.id);
    for (const id of cur.filter((id) => !newIds.includes(id))) await removeWorkItemAssignee(itemId, id);
    await reloadItem();
  };
  const saveTitle = async () => { setEditingTitle(false); if (titleDraft.trim() && titleDraft !== item.title) await patch({ title: titleDraft.trim() }); };
  const saveDesc = async () => { setEditingDesc(false); await patch({ description: descDraft || null }); };

  const addSubItem = async () => {
    if (!canEdit) { toast.error('沒有編輯權限'); return; }
    const t = newSubTitle.trim(); if (!t) return;
    const { error } = await createWorkItem({ title: t, type: 'task', project_id: item.project_id, parent_id: item.id, group_id: item.group_id, company_id: companyId, department_id: departmentId, creator_id: profile?.id, assignee_ids: profile?.id ? [profile.id] : [] });
    if (error) { toast.error('新增子項失敗'); return; }
    setNewSubTitle('');
    const ch = await getWorkItemChildren(itemId); setChildren(ch.data || []); onChanged?.();
  };
  // 子項無詳情頁可手動改狀態,勾選完成/取消時同步 status(closed/open),避免母項關閉後子項狀態卡在 open 一直出現在篩選內
  const toggleSub = async (sub) => {
    if (!canEdit) { toast.error('沒有編輯權限'); return; }
    const next = !sub.is_completed;
    const status = next ? 'closed' : 'open';
    const completed_at = next ? new Date().toISOString() : null;
    const newList = children.map((c) => (c.id === sub.id ? { ...c, is_completed: next, status, completed_at } : c));
    setChildren(newList);
    await updateWorkItem(sub.id, { is_completed: next, status, completed_at });
    if (!next) await ensureParentReopen(newList); // 取消完成 → 若父已 closed 一併 reopen
    onChanged?.();
  };
  const patchChild = async (id, updates) => {
    if (!canEdit) { toast.error('沒有編輯權限'); return; }
    setChildren((prev) => prev.map((c) => (c.id === id ? { ...c, ...updates } : c)));
    await updateWorkItem(id, updates);
    onChanged?.();
  };
  const reloadChildren = async () => { const ch = await getWorkItemChildren(itemId); const list = ch.data || []; setChildren(list); await ensureParentReopen(list); onChanged?.(); };
  // 子項 card 未存保護:切換到別的子項前,若目前 card 有未存變更先提示
  const subDirtyRef = useRef(false);
  const setSubDirty = useCallback((d) => { subDirtyRef.current = d; }, []);
  const requestEditSub = async (sub) => {
    if (editingSub && sub?.id !== editingSub.id && subDirtyRef.current) {
      const ok = await confirm({ title: '尚未儲存', message: '此子項有未儲存的變更,要放棄並切換嗎?', confirmText: '放棄變更', type: 'danger' });
      if (!ok) return;
    }
    subDirtyRef.current = false;
    setEditingSub(sub);
  };
  // ── 子項拖曳排序(與列表同一套:全域寫 work_items.display_order)──
  // Drawer 一定是單一母項底下的完整清單(getWorkItemChildren 不濾 closed),
  // 所以不像列表那樣需要補撈被隱藏的項目;仍走同一支 API 保持行為一致。
  const [dragSubId, setDragSubId] = useState(null);
  const [dragOverSubId, setDragOverSubId] = useState(null);

  const handleSubDrop = async (targetId) => {
    const fromId = dragSubId;
    setDragSubId(null); setDragOverSubId(null);
    if (!fromId || fromId === targetId) return;
    const ids = children.map((c) => c.id);
    const fi = ids.indexOf(fromId);
    const ti = ids.indexOf(targetId);
    if (fi < 0 || ti < 0) return;
    const [moved] = ids.splice(fi, 1);
    ids.splice(ti, 0, moved);
    // 先動畫面再寫 DB,拖完不要等一個 round trip 才看到結果
    const byId = new Map(children.map((c) => [c.id, c]));
    setChildren(ids.map((id) => byId.get(id)).filter(Boolean));
    const { data: all } = await getAllChildrenForOrder(itemId);
    const full = (all || []).sort(compareWorkItemSiblings);
    const { error } = await updateWorkItemOrder(mergeSiblingOrder(full, ids));
    if (error) { toast.error('排序儲存失敗'); await reloadChildren(); return; }
    onChanged?.();
  };

  const setChildLocal = (id, field, val) => setChildren((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: val } : c)));
  const saveChildHours = async (id, field, raw) => {
    if (!canEdit) return;
    const num = raw === '' || raw == null ? null : Number(raw);
    await updateWorkItem(id, { [field]: num });
    onChanged?.();
  };
  const deleteSub = async (sub) => {
    if (!canEdit) { toast.error('沒有編輯權限'); return; }
    const ok = await confirm({ title: '刪除子項', message: `確定刪除「${sub.title}」?`, confirmText: '刪除', danger: true });
    if (!ok) return;
    await deleteWorkItem(sub.id); setChildren((prev) => prev.filter((c) => c.id !== sub.id)); onChanged?.();
  };
  const reloadLogs = async () => { const dl = await getWorkItemDeliveryLogs(itemId); setLogs(dl.data || []); };
  const resetLogForm = () => { setLogForm({ label: 'Frontend', version: '', environment: 'staging', note: '' }); setLogFiles([]); if (logFileRef.current) logFileRef.current.value = ''; };
  const addLog = async () => {
    if (!canEdit) { toast.error('沒有編輯權限'); return; }
    if (!logForm.label) return;
    const { data, error } = await createWorkItemDeliveryLog({ work_item_id: itemId, user_id: profile?.id, label: logForm.label, version: logForm.version, environment: logForm.environment, note: logForm.note, company_id: companyId, department_id: departmentId });
    if (error) { toast.error('新增交付紀錄失敗'); return; }
    for (const f of logFiles) { await uploadWorkItemAttachment(itemId, profile?.id, f, data.id); }
    setAddingLog(false); resetLogForm();
    await reloadLogs();
  };
  const startEditLog = (l) => { if (!canEdit) { toast.error('沒有編輯權限'); return; } setAddingLog(false); setEditingLogId(l.id); setLogFiles([]); setLogForm({ label: l.label, version: l.version || '', environment: l.environment || 'staging', note: l.note || '' }); };
  const cancelEditLog = () => { setEditingLogId(null); resetLogForm(); };
  const saveEditLog = async () => {
    if (!logForm.label) return;
    const { error } = await updateWorkItemDeliveryLog(editingLogId, logForm);
    if (error) { toast.error('更新失敗'); return; }
    for (const f of logFiles) { await uploadWorkItemAttachment(itemId, profile?.id, f, editingLogId); }
    setEditingLogId(null); resetLogForm();
    await reloadLogs();
  };
  const removeLogAttach = async (att) => {
    const ok = await confirm({ title: '刪除附件', message: `確定刪除「${att.file_name}」?`, confirmText: '刪除', type: 'danger' });
    if (!ok) return;
    await deleteWorkItemAttachment(att.id, att.file_path); await reloadLogs();
  };

  const addComment = async () => {
    const c = newComment.trim(); if (!c) return;
    const { data, error } = await createWorkItemComment({ work_item_id: itemId, author_id: profile?.id, content: c });
    if (error) { toast.error('留言失敗'); return; }
    setComments((prev) => [...prev, data]); setNewComment('');
  };
  // 留言清單一變就重撈引用到的附件(新留言剛送出、編輯後移除、別人刪掉都要反映)
  useEffect(() => {
    const ids = [...new Set(comments.flatMap((c) => parseAttachmentIds(c.content)))];
    if (!ids.length) { setCommentAttachments(new Map()); return undefined; }
    let cancelled = false;
    getWorkItemAttachmentsByIds(ids).then(({ data }) => {
      if (!cancelled) setCommentAttachments(new Map((data || []).map((a) => [a.id, a])));
    });
    return () => { cancelled = true; };
  }, [comments]);

  // 拖曳 / 貼上非圖片檔案 → 傳成留言附件,回傳 markdown 給 MentionInput 插進內文。
  // 內文存的是 attachment id 不是 URL(signed URL 一小時就過期,寫進內文明天就打不開)。
  const uploadCommentFile = useCallback(async (file) => {
    const { data, error } = await uploadWorkItemCommentAttachment(itemId, profile?.id, file);
    if (error || !data) return { error: error?.message || '上傳失敗' };
    return { markdown: buildAttachmentMarkup(data.file_name, data.id) };
  }, [itemId, profile?.id]);

  // chip 點擊:此刻才跟 Storage 換一張 signed URL(帶 Content-Disposition 強制下載)
  const openCommentAttachment = useCallback(async (attachmentId) => {
    const att = commentAttachments?.get(attachmentId);
    if (!att) { toast.error('附件已移除'); return; }
    const { url, error } = await getWorkItemAttachmentUrl(att.file_path, att.file_name);
    if (error || !url) { toast.error('無法取得下載連結'); return; }
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [commentAttachments, toast]);

  const startEditComment = (c) => { setEditingCommentId(c.id); setEditCommentDraft(c.content); };
  const cancelEditComment = () => { setEditingCommentId(null); setEditCommentDraft(''); };
  const saveEditComment = async () => {
    const content = editCommentDraft.trim();
    if (!content) return;
    // 內文裡的附件 chip 被刪掉 = 檔案連 Storage 一起永久刪除(不像圖片刪了 bucket 還在),
    // 使用者多半是在改錯字時誤刪,所以先問一次。
    const before = comments.find((c) => c.id === editingCommentId)?.content || '';
    const kept = new Set(parseAttachmentIds(content));
    // 別則留言還在引用的不算刪除 —— 擁有權會轉移過去,檔案不會消失,不要嚇使用者
    const referencedElsewhere = new Set(
      comments.filter((c) => c.id !== editingCommentId).flatMap((c) => parseAttachmentIds(c.content)),
    );
    const removing = parseAttachmentIds(before)
      .filter((id) => !kept.has(id) && !referencedElsewhere.has(id))
      .map((id) => commentAttachments?.get(id))
      .filter(Boolean);
    if (removing.length) {
      const names = removing.map((a) => `・${a.file_name}`).join('\n');
      const ok = await confirm({
        title: '附件將一併刪除',
        message: `這次編輯移除了 ${removing.length} 個附件,儲存後檔案會永久刪除,無法復原:\n\n${names}`,
        confirmText: '仍要儲存',
        type: 'danger',
      });
      if (!ok) return;
    }
    const { data, error } = await updateWorkItemComment(editingCommentId, content);
    if (error) { toast.error('更新失敗'); return; }
    setComments((prev) => prev.map((c) => (c.id === editingCommentId ? { ...c, ...data } : c)));
    setEditingCommentId(null); setEditCommentDraft('');
  };
  const removeComment = async (id) => {
    const ok = await confirm({ title: '刪除留言', message: '確定要刪除此留言嗎?此操作無法復原。', confirmText: '刪除', type: 'danger' });
    if (!ok) return;
    await deleteWorkItemComment(id); setComments((prev) => prev.filter((c) => c.id !== id));
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setUploading(true);
    for (const f of files) { const { error } = await uploadWorkItemAttachment(itemId, profile?.id, f); if (error) toast.error(`上傳失敗:${f.name}`); }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    const { data } = await getWorkItemAttachments(itemId); setAttachments(data || []);
  };
  const openAttach = async (a) => {
    const { url, error } = await getWorkItemAttachmentUrl(a.file_path, a.file_name);
    if (!url) { console.warn('[workItem] attachment url failed', error); toast.error('開啟附件失敗'); return; }
    // anchor 觸發下載:不受彈窗攔截、不留空白分頁、standalone/內嵌都穩
    const link = document.createElement('a');
    link.href = url; link.rel = 'noopener'; link.target = '_blank';
    document.body.appendChild(link); link.click(); link.remove();
  };
  const removeAttach = async (a) => {
    const ok = await confirm({ title: '刪除附件', message: `確定要刪除「${a.file_name}」嗎?此操作無法復原。`, confirmText: '刪除', type: 'danger' });
    if (!ok) return;
    await deleteWorkItemAttachment(a.id, a.file_path); setAttachments((prev) => prev.filter((x) => x.id !== a.id));
  };
  const ACTION_LABELS = { create: '建立', update: '更新', delete: '刪除', assign: '指派', unassign: '取消指派', assignment_change: '變更指派', status_change: '變更狀態' };
  const activityText = (a) => {
    const ch = a.changes || {};
    switch (a.action) {
      case 'assign': return <>指派了 <b>{ch.user_id ? userName(ch.user_id) : '成員'}</b></>;
      case 'unassign': return <>移除了 <b>{ch.user_id ? userName(ch.user_id) : '成員'}</b> 的指派</>;
      case 'assignment_change': return <>將 <b>{ch.owner_id ? userName(ch.owner_id) : '成員'}</b> 設為負責人</>;
      case 'status_change': return <>將狀態改為 <b>{statusMeta(ch.after?.status).label}</b></>;
      case 'create': return <>建立了此工作項目</>;
      case 'update':
        if (ch.reference) return <>{ch.reference.op === 'add' ? '新增' : '移除'}了關聯 <b>{ch.reference.label}</b></>;
        return <>{ACTION_LABELS[a.action] || a.action} 此工作項目</>;
      default: return <>{ACTION_LABELS[a.action] || a.action} 此工作項目</>;
    }
  };

  const avatar = (userId, size = 22) => {
    const u = usersById.get(userId); const name = u?.name || u?.email || '?';
    return <span className="wd-ava" style={{ background: avaColor(userId), width: size, height: size }} title={name}>{u?.avatar_url ? <img src={u.avatar_url} alt={name} /> : name.charAt(0)}</span>;
  };
  const userName = (userId) => usersById.get(userId)?.name || usersById.get(userId)?.email || '未知';

  if (loading || !item) {
    return ReactDOM.createPortal(
      (<><div className="wd-overlay" onClick={onClose} /><div className="wd-drawer"><div className="wd-loading">載入中…</div></div></>),
      document.body,
    );
  }

  const tc = WORK_ITEM_TYPE_COLORS[item.type] || WORK_ITEM_TYPE_COLORS.task;
  const projColor = item.projects?.color || '#667eea';
  const sm = statusMeta(item.status);
  const stFg = getTextColorForBg(sm.color);
  const pm = WORK_ITEM_PRIORITY_META[item.priority];
  const doneCount = children.filter((c) => c.is_completed).length;
  const roleAgg = {}; let totalEst = 0; let totalAct = 0;
  children.forEach((c) => {
    const r = c.role || '未分';
    roleAgg[r] = roleAgg[r] || { est: 0, act: 0 };
    roleAgg[r].est += Number(c.estimated_hours) || 0; roleAgg[r].act += Number(c.actual_hours) || 0;
    totalEst += Number(c.estimated_hours) || 0; totalAct += Number(c.actual_hours) || 0;
  });
  const roleTotalsText = Object.entries(roleAgg).map(([r, v]) => `${r} ${v.est}h/${v.act}h`).join(' · ');
  const sortedAssignees = [...(item.assignees || [])].sort((a, b) => new Date(a.assigned_at || 0) - new Date(b.assigned_at || 0));
  const ownerId = sortedAssignees[0]?.user_id || null;
  const collaboratorIds = sortedAssignees.slice(1).map((a) => a.user_id);
  // specforge 匯入的項目沒有「某個人」當建立者(creator_id 為 null)→ 標記來源 specforge,不顯示「未知」
  const creatorName = usersById.get(item.creator_id)?.name || usersById.get(item.creator_id)?.email || (item.source === 'specforge' ? 'specforge' : '未知');

  // 規格 / 驗收分頁的顯示條件(只有 specforge 匯入且有對應內容才出現)
  const hasSpec = item.source === 'specforge' && (item.screen_md || item.data_structure_md || item.ui_html);
  const hasAcceptance = Array.isArray(item.acceptance_criteria) && item.acceptance_criteria.length > 0;

  return ReactDOM.createPortal((
    <>
      <div className="wd-overlay" onClick={onClose} />
      <div className="wd-drawer">
        {/* ── 頂部 head:#編號(可複製)+ 建立者/時間 + 關閉(比照舊 Ticket header)── */}
        <div className="wd-head">
          <div className="wd-head-left">
            <InlineSelect
              value={item.project_id}
              caret={false}
              trigger={<span className="wd-proj-tag" style={{ background: projColor, color: getTextColorForBg(projColor) }}>{item.projects?.name || '專案'}<CaretIcon color={getTextColorForBg(projColor)} /></span>}
              options={(projects || []).map((p) => ({ value: p.id, label: p.name, color: p.color }))}
              onChange={(v) => patch({ project_id: v })}
            />
            <InlineSelect
              value={item.group_id}
              caret={false}
              trigger={<span className="wd-group-tag">▦ {(groups || []).find((g) => g.id === item.group_id)?.name || '未分組'}<CaretIcon /></span>}
              options={[{ value: null, label: '未分組' }, ...(groups || []).map((g) => ({ value: g.id, label: g.name }))]}
              onChange={(v) => patch({ group_id: v })}
            />
            <button className="wd-no-copy" onClick={copyLink} title="點擊複製連結">#{item.work_item_no} <span className="wd-copy-ico">⧉</span></button>
            <span className="wd-head-meta">建立者：{creatorName}　建立時間：{fmtDateTime(item.created_at)}</span>
          </div>
          <button
            className={`wd-watch-btn${isWatching ? ' watching' : ''}`}
            onClick={toggleWatch}
            title={isWatching ? '追蹤中:有新留言會通知你(點擊取消追蹤)' : '追蹤後,有新留言會通知你'}
          >
            {isWatching ? '🔔 追蹤中' : '🔕 追蹤'}
          </button>
          <button className="wd-close-x" onClick={onClose} aria-label="關閉">✕</button>
        </div>

        <div className="wd-body">
          {/* ── 左欄 ── */}
          <div className="wd-main">
            {/* 標題區:只留標題 + 專案名小字 */}
            <div className="wd-titleblock">
              <div className="wd-title-line">
                {editingTitle ? (
                  <input className="wd-title-input" autoFocus value={titleDraft}
                    onChange={(e) => setTitleDraft(e.target.value)} onBlur={saveTitle}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) saveTitle(); if (e.key === 'Escape') { setTitleDraft(item.title); setEditingTitle(false); } }} />
                ) : (
                  <h1 className="wd-title" onClick={() => setEditingTitle(true)}>{item.title}</h1>
                )}
              </div>
            </div>

            <div className="wd-divider" />

            {/* Tabs:概覽 / 規格 / 驗收 / 子項(子項本身不再有子項,故 sub-item 開啟時不顯示子項分頁)。
                規格、驗收只有 specforge 匯入且有對應內容的項目才顯示。*/}
            <div className="wd-tabs">
              <button className={tab === 'overview' ? 'active' : ''} onClick={() => setTab('overview')}>概覽</button>
              {hasSpec && (
                <button className={tab === 'spec' ? 'active' : ''} onClick={() => setTab('spec')}>規格</button>
              )}
              {hasAcceptance && (
                <button className={tab === 'acceptance' ? 'active' : ''} onClick={() => setTab('acceptance')}>驗收 <span className="wd-tab-badge">{item.acceptance_criteria.length}</span></button>
              )}
              {!item?.parent_id && (
                <button className={tab === 'subitems' ? 'active' : ''} onClick={() => setTab('subitems')}>子項 {children.length > 0 && <span className="wd-tab-badge">{children.length}</span>}</button>
              )}
              {apEnabled && (
                <button className={tab === 'autopilot' ? 'active' : ''} onClick={() => setTab('autopilot')}>🤖 AI 修復</button>
              )}
            </div>

            {tab === 'overview' && (
              <>
                {/* 描述 */}
                <div className="wd-section">
                  <div className="wd-label-row"><span className="wd-label">描述</span>{!editingDesc && <button className="wd-edit-btn" onClick={() => setEditingDesc(true)}>編輯</button>}</div>
                  {editingDesc ? (
                    <div className="wd-desc-editor">
                      <MarkdownEditor value={descDraft} onChange={setDescDraft} height="220px" placeholder="輸入說明（支援 Markdown）…" linkTickets={false} />
                      <div className="wd-desc-actions"><button className="wd-btn-primary" onClick={saveDesc}>儲存</button><button className="wd-btn-secondary" onClick={() => { setDescDraft(item.description || ''); setEditingDesc(false); }}>取消</button></div>
                    </div>
                  ) : (
                    <div className="wd-desc">{item.description ? <MarkdownViewer content={item.description} linkTickets={false} /> : <span className="wd-muted">尚無說明,點右上「編輯」新增</span>}</div>
                  )}
                </div>

                <div className="wd-divider" />

                {/* 附件 */}
                <div className="wd-section">
                  <div className="wd-label-row">
                    <span className="wd-label">附件 <span className="wd-count">{attachments.length}</span></span>
                    <button className="wd-edit-btn" onClick={() => fileInputRef.current?.click()} disabled={uploading}>{uploading ? '上傳中…' : '＋ 上傳'}</button>
                    <input ref={fileInputRef} type="file" multiple hidden onChange={handleUpload} />
                  </div>
                  {attachments.length === 0 ? (
                    <div className="wd-muted wd-att-empty">尚無附件</div>
                  ) : (
                    <div className="wd-att-list">
                      {attachments.map((a) => (
                        <div className="wd-att" key={a.id}>
                          <span className="wd-att-name" onClick={() => openAttach(a)} title={a.file_name}>📄 {a.file_name}</span>
                          <button className="wd-att-del" onClick={() => removeAttach(a)}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* 規格分頁:specforge 規格快照(匯入時存下;唯讀)。淺灰底 = specforge 的資訊,不可在此異動。*/}
            {tab === 'spec' && hasSpec && (
              <div style={{ background: '#f6f7f9', padding: '10px 14px 14px' }}>
                <div className="wd-label-row" style={{ marginBottom: 2 }}>
                  <span className="wd-muted" style={{ fontSize: 12 }}>來自 specforge・唯讀</span>
                </div>
                {item.screen_md && (
                  <div className="wd-section">
                    <div className="wd-label-row"><span className="wd-label">畫面說明</span></div>
                    <div className="wd-desc"><MarkdownViewer content={item.screen_md} linkTickets={false} /></div>
                  </div>
                )}
                {item.data_structure_md && (
                  <div className="wd-section">
                    <div className="wd-label-row"><span className="wd-label">資料結構</span></div>
                    <div className="wd-desc"><MarkdownViewer content={item.data_structure_md} linkTickets={false} /></div>
                  </div>
                )}
                {item.ui_html && (
                  <div className="wd-section">
                    <div className="wd-label-row">
                      <span className="wd-label">UI 設計稿</span>
                      <button
                        className="wd-edit-btn"
                        onClick={() => window.open(URL.createObjectURL(new Blob([item.ui_html], { type: 'text/html' })), '_blank')}
                      >新分頁開啟</button>
                    </div>
                    <iframe
                      title="UI 設計稿"
                      srcDoc={item.ui_html}
                      sandbox="allow-scripts"
                      className="wd-ui-preview"
                      style={{ width: '100%', height: '520px', border: '1px solid #e4e7ec', borderRadius: 8, background: '#fff' }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* 驗收分頁:結構化驗收條件;題目來自 specforge 唯讀,勾選 pass/fail 由 QA 寫。*/}
            {tab === 'acceptance' && hasAcceptance && (
              <div className="wd-section">
                <div className="wd-label-row">
                  <span className="wd-label">驗收清單</span>
                  <span className="wd-count">
                    已驗 {Object.keys(item.acceptance_results || {}).length}/{item.acceptance_criteria.length}
                  </span>
                </div>
                {AC_CATEGORY_ORDER.map((cat) => {
                  const rows = item.acceptance_criteria.filter((c) => c.category === cat);
                  if (rows.length === 0) return null;
                  return (
                    <div key={cat} className="wd-ac-group">
                      <div className="wd-ac-cat" style={{ color: AC_CATEGORY_META[cat]?.color }}>{cat}</div>
                      {rows.map((c) => {
                        const res = item.acceptance_results?.[c.id];
                        return (
                          <div key={c.id} className="wd-ac-row">
                            <span className="wd-ac-text">{c.text}</span>
                            <div className="wd-ac-btns">
                              {['pass', 'fail'].map((st) => (
                                <button
                                  key={st}
                                  className={`wd-ac-btn${res?.status === st ? ' active' : ''}`}
                                  disabled={acSaving === c.id}
                                  onClick={() => toggleAcceptance(c.id, st)}
                                  style={res?.status === st ? { background: AC_STATUS_META[st].color, borderColor: AC_STATUS_META[st].color, color: '#fff' } : undefined}
                                >
                                  {AC_STATUS_META[st].label}
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}

            {tab === 'subitems' && (
              <div className="wd-section">
                <div className="wd-label-row"><span className="wd-label">子項 · Function List</span>{children.length > 0 && <span className="wd-muted">{doneCount}/{children.length} 完成</span>}</div>
                <div className="wd-fl">
                  <div className="wd-fl-head">
                    <span className="wd-fl-c-item">項目</span>
                    <span className="wd-fl-c-role">角色</span>
                    <span className="wd-fl-c-ass">指派</span>
                    <span className="wd-fl-c-hours">預估 / 實際</span>
                    <span className="wd-fl-c-x" />
                  </div>
                  {children.map((c) => {
                    const rc = WORK_ITEM_ROLE_COLORS[c.role];
                    // Owner = 最早 assigned_at(與列表一致);assignees 巢狀查回來順序不定,不排序會顯示到舊 owner
                    const cOwner = [...(c.assignees || [])].sort((a, b) => new Date(a.assigned_at || 0) - new Date(b.assigned_at || 0))[0];
                    return (
                      <div
                        className={`wd-fl-row${dragOverSubId === c.id ? ' is-dragover' : ''}`}
                        key={c.id}
                        onDragOver={(e) => { if (dragSubId && dragSubId !== c.id) { e.preventDefault(); setDragOverSubId(c.id); } }}
                        onDragLeave={() => { if (dragOverSubId === c.id) setDragOverSubId(null); }}
                        onDrop={() => handleSubDrop(c.id)}
                      >
                        <div className="wd-fl-c-item">
                          <span
                            className="wd-fl-grip"
                            draggable
                            onDragStart={() => setDragSubId(c.id)}
                            onDragEnd={() => { setDragSubId(null); setDragOverSubId(null); }}
                            title="拖曳調整子項順序"
                          >⠿</span>
                          <button className={`wd-check ${c.is_completed ? 'done' : ''}`} onClick={() => toggleSub(c)}>{c.is_completed && <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2"><path d="M2.5 6.5l2.5 2.5 4.5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>}</button>
                          <span className={`wd-fl-title ${c.is_completed ? 'done' : ''}`} onClick={() => requestEditSub(c)} title="點擊編輯子項(說明/負責人/日期)">{c.title}</span>
                        </div>
                        <div className="wd-fl-c-role">
                          <InlineSelect
                            value={c.role} caret={false}
                            trigger={c.role ? <span className="wd-role" style={rc ? { background: rc.bg, color: rc.fg } : undefined}>{c.role}</span> : <span className="wd-role wd-role-empty">＋角色</span>}
                            options={[{ value: null, label: '—' }, ...WORK_ITEM_ROLES.map((r) => ({ value: r, label: r, color: WORK_ITEM_ROLE_COLORS[r].fg }))]}
                            onChange={(v) => patchChild(c.id, { role: v })}
                          />
                        </div>
                        <div className="wd-fl-c-ass">{cOwner ? avatar(cOwner.user_id, 22) : <span className="wd-muted">—</span>}</div>
                        <div className="wd-fl-c-hours">
                          <input type="number" min="0" step="0.5" className="wd-h-input" placeholder="估" value={c.estimated_hours ?? ''} onChange={(e) => setChildLocal(c.id, 'estimated_hours', e.target.value === '' ? null : Number(e.target.value))} onBlur={(e) => saveChildHours(c.id, 'estimated_hours', e.target.value)} />
                          <span className="wd-h-sep">/</span>
                          <input type="number" min="0" step="0.5" className="wd-h-input" placeholder="實" value={c.actual_hours ?? ''} onChange={(e) => setChildLocal(c.id, 'actual_hours', e.target.value === '' ? null : Number(e.target.value))} onBlur={(e) => saveChildHours(c.id, 'actual_hours', e.target.value)} />
                        </div>
                        <button className="wd-fl-c-x wd-sub-del" onClick={() => deleteSub(c)}>✕</button>
                      </div>
                    );
                  })}
                  {children.length > 0 && (
                    <div className="wd-fl-total">
                      <span className="wd-fl-total-label">合計</span>
                      <span className="wd-fl-total-roles">{roleTotalsText}</span>
                      <span className="wd-fl-total-sum">{totalEst}h / {totalAct}h</span>
                    </div>
                  )}
                  <div className="wd-addsub"><input value={newSubTitle} placeholder="＋ 新增子項…" onChange={(e) => setNewSubTitle(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) addSubItem(); }} /></div>
                </div>
              </div>
            )}

            {tab === 'autopilot' && (
              <div className="wd-section">
                {apLoading ? (
                  <div className="wd-muted">載入中…</div>
                ) : !apRequest ? (
                  <>
                    <p className="wd-muted" style={{ marginBottom: 10 }}>
                      AI 會依這張工作項目的標題/描述直接改 code、build 過才推回 main。
                    </p>
                    {/* 預覽角色(三態):backend-jwt 要選登入角色才截得了需登入的頁;none 免登入直接截;
                        未設 previewAuth 則不顯示(不產生截圖)。 */}
                    {previewAuthType === 'backend-jwt' && (
                      apPreviewAccounts.length > 0 ? (
                        <div style={{ marginBottom: 10 }}>
                          <span className="wd-label" style={{ display: 'block', marginBottom: 4 }}>以哪個角色預覽</span>
                          <CustomSelect
                            value={apPreviewRole}
                            onChange={(v) => setApPreviewRole(v || '')}
                            options={[{ value: '', label: '不產生截圖預覽' }, ...apPreviewAccounts.map((a) => ({ value: a.id, label: a.label }))]}
                            portal
                          />
                        </div>
                      ) : (
                        <p className="wd-muted" style={{ marginBottom: 10, fontSize: 12 }}>
                          這個專案還沒登記預覽測試帳號,將不產生截圖(可到專案設定登記後再送)。
                        </p>
                      )
                    )}
                    <button className="wd-btn-primary" onClick={sendAiFix} disabled={apSending}>
                      {apSending ? '送出中…' : '🤖 送 AI 修復'}
                    </button>
                  </>
                ) : (
                  <>
                    <div className="wd-label-row">
                      <span className="wd-label">狀態</span>
                      <span className="wd-muted">{AUTOPILOT_STATUS_LABEL[apRequest.status] || apRequest.status}</span>
                    </div>
                    {/* 需求與補充說明:原始需求 + 每則追加,讓使用者看得到當初打了什麼 */}
                    <div style={{ margin: '8px 0' }}>
                      <span className="wd-label" style={{ display: 'block', marginBottom: 4 }}>需求與補充說明</span>
                      {apRequest.spec && (
                        <div style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: 'rgba(0,0,0,0.03)', borderRadius: 6, padding: '6px 8px', marginBottom: 6 }}>
                          <div className="wd-muted" style={{ fontSize: 11, marginBottom: 2 }}>原始需求</div>
                          {apRequest.spec}
                        </div>
                      )}
                      {apMessages.map((m) => (
                        <div key={m.id} style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: 'rgba(0,0,0,0.03)', borderRadius: 6, padding: '6px 8px', marginBottom: 6 }}>
                          <div className="wd-muted" style={{ fontSize: 11, marginBottom: 2 }}>補充 · {m.author_name || '—'} · {m.created_at ? new Date(m.created_at).toLocaleString() : ''}</div>
                          {m.text}
                        </div>
                      ))}
                    </div>
                    {apStatusLoading ? (
                      <p className="wd-muted" style={{ marginTop: 8 }}>載入改動內容…</p>
                    ) : apPipelineStatus?.files?.length > 0 ? (
                      <AutopilotDiffViewer files={apPipelineStatus.files} />
                    ) : apRequest.status === 'running' ? (
                      <p className="wd-muted" style={{ marginTop: 8 }}>AI 改動中…改完會在這裡列出更動的檔案與 diff。</p>
                    ) : apRequest.status === 'committed' || apRequest.status === 'deployed' ? (
                      <p className="wd-muted" style={{ marginTop: 8 }}>這次沒有產生檔案改動。</p>
                    ) : null}
                    {/* 實際畫面預覽(after 截圖):讓 PM 不用只看 diff 猜長怎樣。截圖非同步產生,
                        修完後才會陸續回填;有 previewShots 才顯示。 */}
                    {apPipelineStatus?.previewShots?.length > 0 && (
                      <div style={{ marginTop: 14 }}>
                        <span className="wd-label" style={{ display: 'block', marginBottom: 6 }}>實際畫面預覽</span>
                        {apPipelineStatus.previewShots.map((s, i) => (
                          <div key={i} style={{ marginBottom: 12 }}>
                            <div className="wd-muted" style={{ fontSize: 12, marginBottom: 4 }}>
                              {s.path}{s.note ? ` — ${s.note}` : ''}
                            </div>
                            {s.url ? (
                              <a href={s.url} target="_blank" rel="noreferrer">
                                <img src={s.url} alt={s.path} style={{ maxWidth: '100%', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 6, display: 'block' }} />
                              </a>
                            ) : (
                              <p className="wd-muted" style={{ fontSize: 12, margin: 0, color: '#c0392b' }}>截圖失敗:{s.error || '未知原因'}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {/* 修完但截圖還沒回來:提示等待(有選角色 / 有影響路由才會有截圖) */}
                    {(apRequest.status === 'committed' || apRequest.status === 'deployed')
                      && !apPipelineStatus?.previewShots?.length
                      && apPipelineStatus?.affectedRoutes?.length > 0 && (
                      <p className="wd-muted" style={{ marginTop: 10, fontSize: 12 }}>實際畫面預覽產生中…(影響 {apPipelineStatus.affectedRoutes.length} 個頁面)</p>
                    )}
                    {/* 失敗:直接把原因(claude 的疑問 / 沒產生改動 / build 錯誤)顯示出來,不用點進 GitLab 翻 log */}
                    {apRequest.status === 'failed' && (
                      <div style={{ marginTop: 8 }}>
                        {apRequest.failure_reason ? (
                          <>
                            <span className="wd-label">失敗原因</span>
                            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, lineHeight: 1.5, background: 'rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 6, padding: '8px 10px', margin: '4px 0 0', maxHeight: 260, overflow: 'auto' }}>
                              {apRequest.failure_reason}
                            </pre>
                          </>
                        ) : (
                          <p className="wd-muted" style={{ margin: 0 }}>這次沒有擷取到失敗原因摘要。</p>
                        )}
                        {(apPipelineStatus?.pipeline?.url || apRequest.pipeline_url) && (
                          <a href={apPipelineStatus?.pipeline?.url || apRequest.pipeline_url} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 6, fontSize: 12 }}>
                            查看完整 pipeline log ↗
                          </a>
                        )}
                      </div>
                    )}
                    {/* 繼續討論 = 唯一的迭代入口,開放到所有非終結狀態(含已部署、失敗):
                        同一分支、AI 保留上下文;取代「重新送出(冷啟動)」與 clone。
                        已部署後也能繼續改 → 改完狀態回 committed,重新部署即可,不封死對話。 */}
                    {['committed', 'running', 'deployed', 'failed'].includes(apRequest.status) && (
                      <div className="wd-comment-input" style={{ marginTop: 12 }}>
                        <textarea
                          value={apDiscussText}
                          onChange={(e) => setApDiscussText(e.target.value)}
                          placeholder={
                            apRequest.status === 'failed'
                              ? '針對上面的失敗原因補充說明,AI 會在同一分支重試…'
                              : apRequest.status === 'deployed'
                                ? '部署後想再調整?補充說明,AI 會在同一分支繼續改,改完重新部署即可…'
                                : '還沒改對?補充說明,AI 會在同一分支繼續改…'
                          }
                          rows={3}
                        />
                        <button className="wd-btn-primary" onClick={sendDiscuss} disabled={apSending || !apDiscussText.trim()}>
                          {apSending ? '送出中…' : '繼續討論'}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="wd-divider" />

            {/* 討論(常駐:留言 / 活動紀錄 子分頁)*/}
            <div className="wd-section wd-discussion">
              <div className="wd-disc-tabs">
                <button className={discTab === 'comments' ? 'active' : ''} onClick={() => setDiscTab('comments')}>💬 留言 {comments.length > 0 && <span className="wd-tab-badge">{comments.length}</span>}</button>
                <button className={discTab === 'activity' ? 'active' : ''} onClick={() => setDiscTab('activity')}>📜 活動紀錄</button>
              </div>
              {discTab === 'comments' ? (
                <div className="wd-comments">
                  {comments.map((c) => (
                    <div className="wd-comment" key={c.id}>
                      {avatar(c.author_id, 28)}
                      <div className="wd-comment-body">
                        <div className="wd-comment-head">
                          <span className="wd-comment-author">{userName(c.author_id)}</span>
                          <span className="wd-comment-time">{fmtTime(c.created_at)}{c.updated_at && c.updated_at !== c.created_at && <span className="wd-comment-edited">（已編輯）</span>}</span>
                          {c.author_id === profile?.id && editingCommentId !== c.id && (
                            <span className="wd-comment-actions">
                              <button className="wd-comment-edit" onClick={() => startEditComment(c)}>編輯</button>
                              <button className="wd-comment-del" onClick={() => removeComment(c.id)}>✕</button>
                            </span>
                          )}
                        </div>
                        {editingCommentId === c.id ? (
                          <div className="wd-comment-edit-box">
                            <MentionInput
                              value={editCommentDraft}
                              onChange={setEditCommentDraft}
                              onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); saveEditComment(); } if (e.key === 'Escape') cancelEditComment(); }}
                              placeholder="編輯留言（Ctrl+Enter 儲存）…"
                              departmentId={profile?.department_id}
                              companyId={companyId}
                              enableTicketRef={false}
                              onFileUpload={uploadCommentFile}
                            />
                            <div className="wd-comment-edit-actions">
                              <button className="wd-btn-secondary" onClick={cancelEditComment}>取消</button>
                              <button className="wd-btn-primary" onClick={saveEditComment} disabled={!editCommentDraft.trim()}>儲存</button>
                            </div>
                          </div>
                        ) : (
                          <div className="wd-comment-text">
                            <MarkdownViewer
                              content={c.content}
                              linkTickets={false}
                              attachments={commentAttachments}
                              onAttachmentClick={openCommentAttachment}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="wd-comment-input">
                    <MentionInput
                      value={newComment}
                      onChange={setNewComment}
                      onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); addComment(); } }}
                      placeholder="留言（Ctrl+Enter 送出,@提及,可貼上或拖曳圖片與檔案）…"
                      departmentId={profile?.department_id}
                      companyId={companyId}
                      enableTicketRef={false}
                      onFileUpload={uploadCommentFile}
                    />
                    <button className="wd-btn-primary" onClick={addComment} disabled={!newComment.trim()}>送出</button>
                  </div>
                </div>
              ) : (
                <div className="wd-activities">
                  {activities.length === 0 ? (
                    <div className="wd-muted wd-att-empty">尚無活動紀錄</div>
                  ) : [...activities].sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)).map((a) => (
                    <div className="wd-activity" key={a.id}>
                      <span className="wd-act-dot" />
                      <div className="wd-act-body">
                        <span className="wd-act-text"><b>{a.user_name || '未知'}</b> {activityText(a)}</span>
                        <span className="wd-act-time">{fmtTime(a.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── 右欄 sidebar ── */}
          <div className="wd-sidebar">
            {/* 狀態（獨立一行）*/}
            <div className="wd-sb-field">
              <span className="wd-sb-label">狀態</span>
              <InlineSelect
                value={item.status}
                caret={false}
                trigger={<span className="wd-status-pill" style={{ background: sm.color, color: stFg }}>{sm.label}<CaretIcon color={stFg} /></span>}
                options={(statusOptions || []).filter((s) => s.value !== 'completed').map((s) => ({ value: s.value, label: s.label, color: s.color }))}
                onChange={(v) => patch({ status: v, completed_at: v === 'rd_fixed' ? new Date().toISOString() : null })}
              />
            </div>
            <div className="wd-sb-field">
              <span className="wd-sb-label">Owner</span>
              {ownerId ? (
                <div className="wd-assignee-row wd-clickable" onClick={() => setAssignMode(assignMode === 'owner' ? null : 'owner')}>
                  <Avatar avatarUrl={usersById.get(ownerId)?.avatar_url} name={userName(ownerId)} size="xsmall" />
                  <span className="wd-assignee-name">{userName(ownerId)}</span>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 5l3 3 3-3" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
              ) : (
                <button className="wd-add-collab" onClick={() => setAssignMode('owner')}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M7 2v10M2 7h10" strokeLinecap="round" /></svg>
                  指派 Owner
                </button>
              )}
              {assignMode === 'owner' && (
                <UserSelect values={[]} onChange={(ids) => changeOwner(ids[0])} users={companyUsers} excludeIds={ownerId ? [ownerId] : []} singleSelect defaultOpen placeholder="選擇 Owner…" />
              )}
            </div>
            <div className="wd-sb-field">
              <span className="wd-sb-label">協作者</span>
              {collaboratorIds.length > 0 && (
                <div className="wd-collab-list">
                  {collaboratorIds.map((id) => (
                    <div className="wd-assignee-row" key={id}>
                      <Avatar avatarUrl={usersById.get(id)?.avatar_url} name={userName(id)} size="xsmall" />
                      <span className="wd-assignee-name">{userName(id)}</span>
                      <button className="wd-remove-x" onClick={() => removeAssignee(id)} title="移除">×</button>
                    </div>
                  ))}
                </div>
              )}
              {assignMode === 'add' ? (
                <UserSelect values={collaboratorIds} onChange={(ids) => changeCollaborators(ids, ownerId)} users={companyUsers} excludeIds={ownerId ? [ownerId] : []} defaultOpen placeholder="新增協作者…" onClose={() => setAssignMode(null)} />
              ) : (
                <button className="wd-add-collab" onClick={() => setAssignMode('add')}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M7 2v10M2 7h10" strokeLinecap="round" /></svg>
                  新增協作者
                </button>
              )}
            </div>
            <div className="wd-sb-field">
              <span className="wd-sb-label">排程</span>
              <div className="wd-dates">
                <input type="date" value={item.start_date || ''} onChange={(e) => patch({ start_date: e.target.value || null })} />
                <span>–</span>
                <input type="date" value={item.end_date || ''} onChange={(e) => patch({ end_date: e.target.value || null })} />
              </div>
            </div>
            {/* 類型 + 優先度（左右排，統一共用 InlinePillSelect 彩色 pill）*/}
            <div className="wd-sp-row">
              <div className="wd-sb-field">
                <span className="wd-sb-label">類型</span>
                <InlinePillSelect
                  value={item.type}
                  options={WORK_ITEM_TYPES.map((t) => ({ value: t, label: WORK_ITEM_TYPE_LABELS[t], color: WORK_ITEM_TYPE_COLORS[t].fg }))}
                  onChange={(v) => patch({ type: v })}
                  trigger={<span className="ips-pill" style={{ background: tc.bg, color: tc.fg }}>◇ {WORK_ITEM_TYPE_LABELS[item.type] || item.type}<PillCaret /></span>}
                />
              </div>
              <div className="wd-sb-field _2">
                <span className="wd-sb-label">優先度</span>
                <InlinePillSelect
                  value={item.priority}
                  options={[0, 1, 2, 3, 4].map((p) => ({ value: p, label: WORK_ITEM_PRIORITY_META[p].label, color: WORK_ITEM_PRIORITY_META[p].color }))}
                  onChange={(v) => patch({ priority: v })}
                  trigger={pm ? <span className="ips-pill" style={{ background: pm.color, color: getTextColorForBg(pm.color) }}>{pm.label}<PillCaret /></span> : <span className="wd-muted">無</span>}
                />
              </div>
            </div>
            {/* 關聯工作項目:手動設定,不再從內文 #編號 自動解析 */}
            <WorkItemReferences
              item={item}
              companyId={companyId}
              userId={profile?.id}
              statusMeta={statusMeta}
              onOpenItem={onOpenItem}
            />

            {/* #5 所屬群組已移到 header（專案右邊），此處移除 */}

            {/* #6 移除「QA 驗證」功能（先註解保留，待 #7 status 改為 rd_fixed / rd_verify 後再決定是否用新狀態重做驗收 UI）
            <div className="wd-card">
              <div className="wd-card-head">
                <span className="wd-card-title">QA 驗證</span>
                <span className="wd-qa-badge" style={{ background: sm.color, color: stFg }}>{item.status === 'completed' ? '已通過' : item.status === 'verify' ? 'QA 測試中' : '尚未驗證'}</span>
              </div>
              <p className="wd-card-desc">確認此工作項目是否符合驗收標準</p>
              {item.status === 'verify' ? (
                <div className="wd-qa-actions">
                  <button className="wd-btn-reject" onClick={() => patch({ status: 'in_progress' })}>退回</button>
                  <button className="wd-btn-pass" onClick={() => patch({ status: 'completed', completed_at: new Date().toISOString(), qa_verified_by: profile?.id, qa_verified_at: new Date().toISOString() })}>通過驗證</button>
                </div>
              ) : item.status === 'completed' ? null : (
                <div className="wd-qa-actions"><button className="wd-btn-pass wd-full" onClick={() => patch({ status: 'verify' })}>送測 待驗證</button></div>
              )}
            </div>
            */}

            {/* 交付紀錄卡 */}
            <div className="wd-card">
              <div className="wd-card-head">
                <span className="wd-card-title">交付紀錄 <span className="wd-count">{logs.length}</span></span>
                <button className="wd-add-log" onClick={() => setAddingLog((v) => !v)}>＋ 新增</button>
              </div>
              {addingLog && (
                <div className="wd-log-form">
                  <CustomSelect value={logForm.label} onChange={(v) => setLogForm((f) => ({ ...f, label: v }))} options={DELIVERY_LABELS.map((l) => ({ value: l, label: l }))} placeholder="標籤" portal />
                  <input placeholder="版本(如 v0.7.0)" value={logForm.version} onChange={(e) => setLogForm((f) => ({ ...f, version: e.target.value }))} />
                  <CustomSelect value={logForm.environment} onChange={(v) => setLogForm((f) => ({ ...f, environment: v }))} options={ENV_OPTIONS} portal />
                  <textarea placeholder="備註(選填)" rows={2} value={logForm.note} onChange={(e) => setLogForm((f) => ({ ...f, note: e.target.value }))} />
                  <label className="wd-log-file">📎 附加檔案<input ref={logFileRef} type="file" multiple hidden onChange={(e) => setLogFiles(Array.from(e.target.files || []))} /></label>
                  {logFiles.length > 0 && <div className="wd-log-files-sel">{logFiles.map((f) => f.name).join('、')}</div>}
                  <button className="wd-btn-primary" onClick={addLog}>新增</button>
                </div>
              )}
              {logs.map((l) => {
                const lc = DELIVERY_LABEL_COLORS[l.label] || '#667085';
                if (editingLogId === l.id) {
                  return (
                    <div className="wd-log-form" key={l.id}>
                      <CustomSelect value={logForm.label} onChange={(v) => setLogForm((f) => ({ ...f, label: v }))} options={DELIVERY_LABELS.map((x) => ({ value: x, label: x }))} portal />
                      <input placeholder="版本" value={logForm.version} onChange={(e) => setLogForm((f) => ({ ...f, version: e.target.value }))} />
                      <CustomSelect value={logForm.environment} onChange={(v) => setLogForm((f) => ({ ...f, environment: v }))} options={ENV_OPTIONS} portal />
                      <textarea placeholder="備註(選填)" rows={2} value={logForm.note} onChange={(e) => setLogForm((f) => ({ ...f, note: e.target.value }))} />
                      {(l.attachments || []).length > 0 && (
                        <div className="wd-log-atts">
                          {l.attachments.map((a) => (
                            <span className="wd-log-att" key={a.id}><span className="wd-log-att-name" onClick={() => openAttach(a)}>📄 {a.file_name}</span><button className="wd-log-att-del" onClick={() => removeLogAttach(a)}>✕</button></span>
                          ))}
                        </div>
                      )}
                      <label className="wd-log-file">📎 附加檔案<input type="file" multiple hidden onChange={(e) => setLogFiles(Array.from(e.target.files || []))} /></label>
                      {logFiles.length > 0 && <div className="wd-log-files-sel">{logFiles.map((f) => f.name).join('、')}</div>}
                      <div className="wd-log-form-actions"><button className="wd-btn-secondary" onClick={cancelEditLog}>取消</button><button className="wd-btn-primary" onClick={saveEditLog}>儲存</button></div>
                    </div>
                  );
                }
                return (
                  <div className="wd-log" key={l.id}>
                    <span className="wd-log-dot" style={{ borderColor: lc }} />
                    <div className="wd-log-body">
                      <div className="wd-log-line">
                        <span className="wd-log-label" style={{ background: lc, color: getTextColorForBg(lc) }}>{l.label}</span>
                        <span className="wd-log-acts">
                          {canEdit && <button className="wd-log-edit" onClick={() => startEditLog(l)} title="編輯">✎</button>}
                          {canDeleteDeliveryLog(profile, l) && <button className="wd-log-del" onClick={async () => { const ok = await confirm({ title: '刪除交付紀錄', message: '確定要刪除這筆交付紀錄嗎?', confirmText: '刪除', type: 'danger' }); if (!ok) return; await deleteWorkItemDeliveryLog(l.id); setLogs((prev) => prev.filter((x) => x.id !== l.id)); }}>✕</button>}
                        </span>
                      </div>
                      {(l.version || l.environment) && (
                        <div className="wd-log-verline">
                          {l.version && <span className="wd-log-ver">{l.version}</span>}
                          {l.environment && <><span className="wd-log-arrow">→</span><span className="wd-log-env">{ENV_LABELS[l.environment] || l.environment}</span></>}
                        </div>
                      )}
                      {l.note && <div className="wd-log-note">{l.note}</div>}
                      {(l.attachments || []).length > 0 && (
                        <div className="wd-log-atts">
                          {l.attachments.map((a) => (
                            <span className="wd-log-att" key={a.id}><span className="wd-log-att-name" onClick={() => openAttach(a)}>📄 {a.file_name}</span></span>
                          ))}
                        </div>
                      )}
                      <span className="wd-log-meta">{userName(l.user_id)}・{fmtTime(l.created_at)}</span>
                    </div>
                  </div>
                );
              })}
              {logs.length === 0 && !addingLog && <span className="wd-muted">尚無交付紀錄</span>}
            </div>
          </div>
        </div>
      </div>

      {editingSub && (
        <SubItemPanel
          key={editingSub.id}
          sub={editingSub}
          companyUsers={companyUsers}
          profile={profile}
          canEdit={canEdit}
          onDirtyChange={setSubDirty}
          onClose={() => { subDirtyRef.current = false; setEditingSub(null); }}
          onSaved={() => { subDirtyRef.current = false; setEditingSub(null); reloadChildren(); }}
        />
      )}
    </>
  ), document.body);
};

export default WorkItemDetailDrawer;
